import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { User, Hash, Mail, Phone, Home, MapPin, Share2, Camera, PawPrint } from 'lucide-react';
import { toast } from 'sonner';

export function ProfileCard({ profile, pictureUrl, onUploadPicture }) {
  const fullAddress = useMemo(() =>
    [profile?.user?.villa_number && `Villa ${profile.user.villa_number}`, profile?.user?.community_name, profile?.user?.community_address].filter(Boolean).join(', '),
    [profile?.user?.villa_number, profile?.user?.community_name, profile?.user?.community_address]
  );

  return (
    <Card className="shadow-lg border-0 overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 p-6">
        <div className="flex items-center gap-5">
          <div className="relative group">
            <div data-testid="profile-avatar" className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-3 border-white/40">
              {pictureUrl ? (
                <img src={pictureUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-white/70" />
              )}
            </div>
            <label data-testid="upload-picture-button" className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center cursor-pointer shadow-md hover:bg-slate-100 transition-colors">
              <Camera className="w-3.5 h-3.5 text-slate-700" />
              <input type="file" accept="image/*" onChange={onUploadPicture} className="hidden" />
            </label>
          </div>
          <div className="flex-1 text-white">
            <h2 className="text-2xl font-bold">{profile?.user?.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="bg-white/20 text-white">{profile?.user?.role?.toUpperCase()}</Badge>
              {profile?.user?.villa_number && <Badge className="bg-white/20 text-white">Villa {profile?.user?.villa_number}</Badge>}
            </div>
          </div>
        </div>
      </div>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Hash className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Unique ID</p>
              <p data-testid="profile-unique-id" className="font-mono font-bold text-sm">{profile?.display_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Mail className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Email</p>
              <p className="font-medium text-sm truncate">{profile?.user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Phone className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Phone</p>
              <p className="font-medium text-sm">{profile?.user?.phone || 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Home className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Villa</p>
              <p className="font-medium text-sm">{profile?.user?.villa_number || 'N/A'}</p>
            </div>
          </div>
        </div>

        {(profile?.user?.community_name || profile?.user?.community_address) && (
          <div data-testid="profile-address-section" className="p-4 bg-gk-bg rounded-lg border border-slate-200">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <MapPin className="w-4 h-4 text-gk-teal mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gk-teal font-medium">Complete Address</p>
                  <p data-testid="profile-full-address" className="text-sm font-semibold text-slate-800 mt-0.5">
                    {fullAddress}
                  </p>
                </div>
              </div>
              <Button data-testid="share-address-button" size="sm" variant="outline" className="shrink-0 border-slate-200 text-gk-teal hover:bg-slate-100"
                onClick={() => {
                  const addr = [profile?.user?.name, fullAddress].filter(Boolean).join(', ');
                  if (navigator.share) {
                    navigator.share({ title: 'My Address', text: addr }).catch(() => { navigator.clipboard.writeText(addr); toast.success('Address copied'); });
                  } else { navigator.clipboard.writeText(addr); toast.success('Address copied'); }
                }}>
                <Share2 className="w-4 h-4 mr-1" /> Share
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PetPhotoAvatar({ petId, hasPetPhoto, getPetPhotoUrl }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!hasPetPhoto) return;
    const { url, token } = getPetPhotoUrl(petId);
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => blob && setSrc(URL.createObjectURL(blob)))
      .catch((err) => { console.error('Failed to load pet photo:', err); });
  }, [petId, hasPetPhoto, getPetPhotoUrl]);
  if (src) return <img src={src} alt="Pet" className="w-10 h-10 rounded-full object-cover border-2 border-orange-200" />;
  return <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center"><PawPrint className="w-4 h-4 text-orange-700" /></div>;
}
